import React from "react";
import { FormControl, InputLabel, Select, MenuItem, Switch, IconButton, Tooltip, Theme, styled } from "@mui/material";
import { SegmentationMethod, Segment } from "../../types/types";
import MDTypography from "../../../../../components/MDTypography";
import MDBox from "../../../../../components/MDBox";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import colors from "assets/theme-dark/base/colors";
import { getOrdinal } from "../../utils/numberUtils";
import { SelectChangeEvent } from "@mui/material/Select";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import { useGroupCounts } from "../../hooks/useSegmentationGroupedCount";
import MiniGroupCountsCard from "./SegmentationTotalCountsCard";
import { cruxyColors } from "../../theme/color-palette";

interface ColumnRange {
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface SegmentationItemProps {
  index: number;
  type: string;
  method: SegmentationMethod;
  segments: Segment[];
  segmentationTypes: string[];
  usedTypes: string[];
  columnRanges: Record<string, ColumnRange>;
  onTypeChange: (event: SelectChangeEvent<string>, index: number) => void;
  onMethodChange: (event: React.ChangeEvent<HTMLInputElement>, index: number) => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  canRemove: boolean;
  isDisabled: boolean;
}

const SegmentationItem: React.FC<SegmentationItemProps> = ({
  index,
  type,
  method,
  segments,
  segmentationTypes,
  usedTypes,
  columnRanges,
  onTypeChange,
  onMethodChange,
  onRemove,
  onEdit,
  canRemove,
  isDisabled,
}) => {
  const { tableName } = useHeadroomContext();
  const methodText = method === "DATA" ? "Data Driven" : "Manual";
  const isNumeric = type && columnRanges[type]?.isNumeric;
  const manualSwitchDisabled = isDisabled || (type && !isNumeric) || !type;

  const availableTypes = segmentationTypes.filter(
    (typeOption) => !usedTypes?.includes(typeOption) || typeOption === type
  );

  const { groupCounts, loading, error } = useGroupCounts({
    tableName,
    columnName: type,
    method,
    segments,
    isNumeric: isNumeric || false,
  });

  return (
    <SegmentBox mb={1.5}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <MDTypography variant="caption" fontWeight="bold">
          Segmentation {index + 1} <StyledSpan>({methodText})</StyledSpan>
        </MDTypography>
        <MDBox display="flex" alignItems="start">
          <div style={{ marginRight: "10px" }}>
            <MDTypography variant="caption">Data</MDTypography>
            <Tooltip
              title={
                type && !isNumeric
                  ? "Please select a numeric column for manual segmentation"
                  : `Switch to ${method === "MANUAL" ? "Data Driven Segmentation" : "Manual Segmentation"}`
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: type && !isNumeric ? colors.error.main : colors.grey[800],
                    color: colors.white.main,
                    fontSize: "0.875rem",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                  },
                },
              }}
              placement="top"
              disableInteractive={false}
            >
              <span>
                <Switch
                  size="small"
                  checked={method === "MANUAL"}
                  onChange={(e) => onMethodChange(e, index)}
                  color="primary"
                  disabled={manualSwitchDisabled}
                />
              </span>
            </Tooltip>
            <MDTypography variant="caption" mr={2}>
              Manual
            </MDTypography>
          </div>
          <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
            <IconButton
              onClick={() => onRemove(index)}
              disabled={!canRemove}
              size="small"
              sx={{ color: cruxyColors.red.main }}
            >
              <Tooltip title={`Delete ${getOrdinal(index + 1)} Segmentation`} disableInteractive>
                <DeleteIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            {method === "MANUAL" && (
              <IconButton onClick={() => onEdit(index)} disabled={manualSwitchDisabled} size="small">
                <Tooltip title={`Edit ${getOrdinal(index + 1)} Segmentation`} disableInteractive>
                  <EditIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
      <MDBox mb={1.5}>
        <FormControl fullWidth size="small">
          <InputLabel>Select column</InputLabel>
          <Select
            value={type}
            onChange={(e) => onTypeChange(e, index)}
            label="Select column"
            sx={{
              width: "50%",
              "& .MuiSelect-select": {
                color: colors.primary.main,
                fontSize: "0.75rem",
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
              <em>None</em>
            </MenuItem>
            {availableTypes.map((typeOption) => (
              <MenuItem key={typeOption} value={typeOption} sx={{ fontSize: "0.75rem" }}>
                {typeOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </MDBox>
      {loading && (
        <MDTypography variant="caption" mt={1}>
          Loading count preview...
        </MDTypography>
      )}
      {error && (
        <MDTypography variant="caption" color="error" mt={1}>
          Error loading count preview
        </MDTypography>
      )}
      {groupCounts.length > 0 && <MiniGroupCountsCard groupCounts={groupCounts} />}
    </SegmentBox>
  );
};

const SegmentBox = styled(MDBox)(({ theme }: { theme: Theme }) => ({
  border: `1px solid ${cruxyColors.amber.main}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[1],
  },
}));

const StyledSpan = styled("span")(() => ({
  color: colors.grey[600],
  fontSize: "0.7rem",
}));

export default SegmentationItem;
